import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import Layout from '../components/common/Layout';
import { tenderAwardeeService, TenderAwardeeDetail } from '../services/tenderAwardeeService';
import { TenderFilters } from '../services/tenderService';
import { callLogService, CreateCallLogData } from '../services/callLogService';
import { FiPhone, FiCalendar, FiMail, FiMapPin, FiMessageSquare, FiArrowLeft, FiEdit2, FiX } from 'react-icons/fi';
import Icon from '../components/common/Icon';
import { formatDate, formatDateTime, getTodayISO, getNowTimeHHMM, datetimeLocalToMysql } from '../utils/dateUtils';
import { getSettingColorClass } from '../utils/settingColors';
import { usePermissions } from '../hooks/usePermissions';
import { useTenderEditModal } from '../hooks/useTenderEditModal';
import { useCallOutcomeOptions } from '../hooks/useCallOutcomeOptions';
import CallStatusSelect from '../components/calls/CallStatusSelect';
import PriorCallAlert from '../components/calls/PriorCallAlert';
import { Tender } from '../types';

type AwardeeNavState = {
  highlightTenderId?: number;
  listRestore?: { page: number; perPage: number; filters: TenderFilters };
};

const TenderAwardeeDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const navState = (location.state as AwardeeNavState | null) ?? {};
  const highlightTenderId = navState.highlightTenderId ?? null;
  const listRestore = navState.listRestore;

  const { hasPermission } = usePermissions();
  const canEditTender = hasPermission('tenders', 'edit');
  const { options: callOutcomeOptions, loading: callOutcomeOptionsLoading } = useCallOutcomeOptions();
  const [awardee, setAwardee] = useState<TenderAwardeeDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCallModal, setShowCallModal] = useState(false);
  const [callSubmitError, setCallSubmitError] = useState('');
  const [callFormData, setCallFormData] = useState<CreateCallLogData>({
    entity_type: 'tender',
    entity_id: 0,
    call_date: getTodayISO(),
    call_time: getNowTimeHHMM(),
    call_type: 'outbound',
  });
  const { openEdit, editModal } = useTenderEditModal(() => loadAwardee());

  useEffect(() => {
    if (id) {
      loadAwardee();
    }
  }, [id]);

  const loadAwardee = async () => {
    try {
      setLoading(true);
      const data = await tenderAwardeeService.getDetail(parseInt(id || '0', 10));
      setAwardee(data);
      setError('');
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(msg || 'Failed to load awardee');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value?: number) => {
    if (value == null || value === 0) return '—';
    return `₹${value.toLocaleString('en-IN')}`;
  };

  const getCallOutcomeColor = (outcome?: string) => {
    switch (outcome) {
      case 'interested':
        return 'bg-green-100 text-green-800';
      case 'not_interested':
        return 'bg-red-100 text-red-800';
      case 'callback':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const backToTenders = () => {
    if (listRestore) {
      navigate('/tenders', { state: { restore: listRestore } });
    } else {
      navigate('/tenders');
    }
  };

  const defaultTenderForCall = (list: Tender[]): Tender | undefined => {
    if (highlightTenderId != null) {
      const highlighted = list.find((t) => t.id === highlightTenderId);
      if (highlighted) return highlighted;
    }
    return list[0];
  };

  const openCallLogModal = (tender?: Tender) => {
    const list = awardee?.tenders || [];
    const selected = tender || defaultTenderForCall(list);
    if (!selected) {
      setError('Add or link a tender before logging a call for this party.');
      return;
    }
    setError('');
    setCallSubmitError('');
    setCallFormData({
      entity_type: 'tender',
      entity_id: selected.id,
      tender_assignment_id: selected.assignment_id ?? undefined,
      call_date: getTodayISO(),
      call_time: getNowTimeHHMM(),
      call_type: 'outbound',
    });
    setShowCallModal(true);
  };

  const handleCallSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!callFormData.entity_id) {
      setCallSubmitError('Select a tender to log this call against.');
      return;
    }
    try {
      setCallSubmitError('');
      await callLogService.create(callFormData);
      setShowCallModal(false);
      setCallFormData({
        entity_type: 'tender',
        entity_id: 0,
        call_date: getTodayISO(),
        call_time: getNowTimeHHMM(),
        call_type: 'outbound',
      });
      await loadAwardee();
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string; error?: string } } }).response?.data?.message
            || (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
      setCallSubmitError(msg || 'Failed to log call');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-8 text-sm text-gray-600">Loading...</div>
      </Layout>
    );
  }

  if (!awardee) {
    return (
      <Layout>
        <div className="text-center py-8 text-sm text-red-600">{error || 'Awardee not found'}</div>
      </Layout>
    );
  }

  const stats = awardee.call_statistics;
  const tenders = awardee.tenders || [];
  const callLogs = awardee.call_logs || [];

  return (
    <Layout>
      <div className="space-y-4 sm:space-y-5">
        <div>
          <button
            type="button"
            onClick={backToTenders}
            className="flex items-center space-x-1 text-gray-600 hover:text-gray-900 mb-3"
          >
            <Icon component={FiArrowLeft} className="w-4 h-4" />
            <span className="text-sm">Back to Tenders</span>
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded text-sm">
            {error}
          </div>
        )}

        {/* Awardee profile */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">{awardee.name}</h1>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
            {awardee.awardee_city && (
              <div className="flex items-center gap-1 text-gray-700">
                <Icon component={FiMapPin} className="w-4 h-4 text-gray-400" />
                <span>{awardee.awardee_city}</span>
              </div>
            )}
            {awardee.awardee_phone && (
              <div className="flex items-center gap-2 flex-wrap">
                <Icon component={FiPhone} className="w-4 h-4 text-gray-400" />
                <span className="text-gray-900">{awardee.awardee_phone}</span>
                <a
                  href={`tel:${awardee.awardee_phone}`}
                  className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm font-medium"
                  title={`Call ${awardee.awardee_phone}`}
                >
                  <Icon component={FiPhone} className="w-4 h-4" />
                  <span>Call</span>
                </a>
                <button
                  type="button"
                  onClick={() => openCallLogModal()}
                  className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-yellow-500 text-black-950 rounded-md hover:bg-yellow-600 text-sm font-medium font-bold"
                  title="Log New Call"
                >
                  <Icon component={FiCalendar} className="w-4 h-4" />
                  <span>Log New Call</span>
                </button>
              </div>
            )}
            {!awardee.awardee_phone && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => openCallLogModal()}
                  className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-yellow-500 text-black-950 rounded-md hover:bg-yellow-600 text-sm font-medium font-bold"
                  title="Log New Call"
                >
                  <Icon component={FiCalendar} className="w-4 h-4" />
                  <span>Log New Call</span>
                </button>
              </div>
            )}
            {awardee.awardee_email && (
              <div className="flex items-center gap-1 text-gray-700 min-w-0">
                <Icon component={FiMail} className="w-4 h-4 text-gray-400 shrink-0" />
                <a href={`mailto:${awardee.awardee_email}`} className="text-yellow-700 hover:underline truncate">
                  {awardee.awardee_email}
                </a>
              </div>
            )}
          </div>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-900">{awardee.tender_count ?? tenders.length}</div>
              <div className="text-xs text-gray-600 mt-1">Tenders</div>
            </div>
            <div className="text-center p-3 bg-indigo-50 rounded-lg">
              <div className="text-lg font-bold text-indigo-600">
                {formatCurrency(awardee.summary?.total_work_order_value)}
              </div>
              <div className="text-xs text-gray-600 mt-1">Total work order</div>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                {awardee.summary?.converted_tender_count ?? 0}
              </div>
              <div className="text-xs text-gray-600 mt-1">Converted tenders</div>
            </div>
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{stats?.total_calls ?? 0}</div>
              <div className="text-xs text-gray-600 mt-1">Total calls</div>
            </div>
          </div>
        </div>

        {/* Call statistics */}
        {stats && (
          <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5">
            <h2 className="text-lg font-bold text-gray-900 mb-3">Call Statistics</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{stats.total_calls || 0}</div>
                <div className="text-xs text-gray-600 mt-1">Total Calls</div>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{stats.answered_calls || 0}</div>
                <div className="text-xs text-gray-600 mt-1">Answered</div>
              </div>
              <div className="text-center p-3 bg-yellow-50 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600">{stats.no_answer_calls || 0}</div>
                <div className="text-xs text-gray-600 mt-1">No Answer</div>
              </div>
              <div className="text-center p-3 bg-purple-50 rounded-lg">
                <div className="text-2xl font-bold text-purple-600">{stats.interested_calls || 0}</div>
                <div className="text-xs text-gray-600 mt-1">Interested</div>
              </div>
            </div>
            {stats.last_call_date && (
              <p className="mt-3 text-sm text-gray-600">
                Last call: {formatDate(stats.last_call_date)}
              </p>
            )}
          </div>
        )}

        {/* Tenders history */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5">
          <h2 className="text-lg font-bold text-gray-900 mb-1">Tenders History</h2>
          <p className="text-sm text-gray-600 mb-4">
            {tenders.length === 0
              ? 'No tenders on record for this awardee.'
              : `${tenders.length} tender${tenders.length === 1 ? '' : 's'} awarded to ${awardee.name}`}
          </p>
          {tenders.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">Date</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">Awarder</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">City</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">Status</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">Converted</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">Brand</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">Work order</th>
                    <th className="px-3 py-2 text-right font-medium text-gray-700"> </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {tenders.map((t) => {
                    const isCurrent = highlightTenderId != null && t.id === highlightTenderId;
                    return (
                      <tr
                        key={t.id}
                        className={isCurrent ? 'bg-yellow-50 hover:bg-yellow-100' : 'hover:bg-gray-50'}
                      >
                        <td className="px-3 py-2 whitespace-nowrap">{formatDate(t.date)}</td>
                        <td className="px-3 py-2 max-w-[200px] truncate" title={t.awarder || ''}>
                          {t.awarder || '—'}
                        </td>
                        <td className="px-3 py-2">{t.city || t.awardee_city || '—'}</td>
                        <td className="px-3 py-2">
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                            {t.status || '—'}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          {(t.conversion_count ?? 0) > 0 || t.conversion_status === 'converted' ? (
                            <span
                              className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-green-100 text-green-800 border border-green-300 text-xs font-medium"
                              title={(t.conversions || []).map((c) => c.customer_code || `Customer ${c.customer_id}`).join(', ') || 'Converted'}
                            >
                              Converted
                            </span>
                          ) : (
                            <span className="text-gray-400">Not converted</span>
                          )}
                        </td>
                        <td className="px-3 py-2">{t.brand_name || '—'}</td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {t.work_order_value != null ? formatCurrency(t.work_order_value) : '—'}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <div className="flex items-center justify-end gap-2">
                            {isCurrent && (
                              <span className="text-xs font-semibold text-yellow-800">Viewing</span>
                            )}
                            <Link
                              to={`/tenders/${t.id}`}
                              state={{
                                assignmentId: t.assignment_id,
                                brandId: t.brand_id ?? undefined,
                                listRestore,
                                highlightTenderId: t.id,
                              }}
                              className="text-yellow-700 hover:underline font-medium"
                            >
                              View
                            </Link>
                            {canEditTender && (
                              <button
                                type="button"
                                onClick={() => openEdit(t as Tender)}
                                className="text-gray-600 hover:text-yellow-700 inline-flex items-center gap-0.5"
                                title="Edit tender"
                                aria-label="Edit tender"
                              >
                                <Icon component={FiEdit2} className="w-3.5 h-3.5" />
                                <span className="text-xs font-medium">Edit</span>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Call history */}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => openCallLogModal()}
            className="flex items-center space-x-2 bg-yellow-500 text-black-950 px-4 py-2 rounded-md hover:bg-yellow-600 text-sm font-medium font-bold"
          >
            <Icon component={FiCalendar} className="w-4 h-4" />
            <span>Log New Call</span>
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Call History</h2>
          {callLogs.length === 0 ? (
            <div className="text-center py-8 text-sm text-gray-500">No call logs yet</div>
          ) : (
            <div className="space-y-4">
              {callLogs.map((log) => (
                <div key={log.id} className="border-l-4 border-indigo-500 pl-4 py-2">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-2 gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <Icon component={FiPhone} className="w-4 h-4 text-gray-400" />
                      <span className="text-sm font-semibold text-gray-900">
                        {formatDateTime(log.call_date, log.call_time)}
                      </span>
                      {log.called_by_name && (
                        <span className="text-xs text-gray-500">by {log.called_by_name}</span>
                      )}
                      {log.entity_id > 0 && (
                        <Link
                          to={`/tenders/${log.entity_id}`}
                          state={{ listRestore }}
                          className="text-xs text-yellow-700 hover:underline"
                        >
                          Tender #{log.entity_id}
                          {log.tender_awarder ? ` · ${log.tender_awarder}` : ''}
                          {log.tender_date ? ` · ${formatDate(log.tender_date)}` : ''}
                        </Link>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {log.call_status && (
                        <span
                          className={`px-2 py-1 rounded text-xs font-semibold border ${getSettingColorClass(log.call_status_color_code)}`}
                        >
                          {log.call_status}
                        </span>
                      )}
                      {log.call_outcome && (
                        <span
                          className={`px-2 py-1 rounded text-xs font-semibold ${getCallOutcomeColor(log.call_outcome)}`}
                        >
                          {log.call_outcome}
                        </span>
                      )}
                    </div>
                  </div>
                  {log.notes && (
                    <div className="text-sm text-gray-700 mt-2 flex items-start space-x-2">
                      <Icon component={FiMessageSquare} className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                      <p className="flex-1">{log.notes}</p>
                    </div>
                  )}
                  {(log.next_call_date || log.next_call_notes) && (
                    <div className="mt-2 text-xs text-indigo-600">
                      {log.next_call_date && (
                        <div className="flex items-center space-x-1">
                          <Icon component={FiCalendar} className="w-3 h-3" />
                          <span>Next call: {formatDateTime(log.next_call_date)}</span>
                        </div>
                      )}
                      {log.next_call_notes && (
                        <p className="mt-0.5 pl-4 text-indigo-800">Next call notes: {log.next_call_notes}</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        {editModal}

        {showCallModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-3 sm:p-4">
            <div className="relative top-2 sm:top-10 mx-auto p-4 sm:p-5 border w-full max-w-md shadow-xl rounded-lg bg-white max-h-[95vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-3 sm:mb-4">
                <h3 className="text-base sm:text-lg font-bold text-gray-900">Log New Call</h3>
                <button
                  type="button"
                  onClick={() => { setShowCallModal(false); setCallSubmitError(''); }}
                  className="text-gray-400 hover:text-gray-600 p-1"
                  aria-label="Close"
                >
                  <Icon component={FiX} className="w-5 h-5" />
                </button>
              </div>
              {callSubmitError && (
                <div className="mb-3 bg-red-50 border border-red-200 text-red-800 px-3 py-2 rounded text-sm">
                  {callSubmitError}
                </div>
              )}
              {callFormData.entity_id > 0 && (
                <PriorCallAlert entityType="tender" entityId={callFormData.entity_id} />
              )}
              <form onSubmit={handleCallSubmit} className="space-y-3 sm:space-y-4">
                {tenders.length > 1 && (
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Tender *</label>
                    <select
                      required
                      value={callFormData.entity_id || ''}
                      onChange={(e) => {
                        const tenderId = parseInt(e.target.value, 10);
                        const selected = tenders.find((t) => t.id === tenderId);
                        setCallFormData({
                          ...callFormData,
                          entity_id: tenderId,
                          tender_assignment_id: selected?.assignment_id ?? undefined,
                        });
                      }}
                      className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                    >
                      {tenders.map((t) => (
                        <option key={t.id} value={t.id}>
                          {formatDate(t.date)} · {t.awarder || 'Tender'} #{t.id}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Call Date *</label>
                    <input
                      type="date"
                      required
                      value={callFormData.call_date}
                      onChange={(e) => setCallFormData({ ...callFormData, call_date: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Call Time</label>
                    <input
                      type="time"
                      value={callFormData.call_time}
                      onChange={(e) => setCallFormData({ ...callFormData, call_time: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Call Status *</label>
                  <CallStatusSelect
                    required
                    value={callFormData.call_status_id}
                    onChange={(call_status_id) => setCallFormData({ ...callFormData, call_status_id })}
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Call Outcome *</label>
                  <select
                    required
                    value={callFormData.call_outcome || ''}
                    onChange={(e) => setCallFormData({ ...callFormData, call_outcome: e.target.value || undefined })}
                    disabled={callOutcomeOptionsLoading}
                    className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm disabled:opacity-60"
                  >
                    <option value="">{callOutcomeOptionsLoading ? 'Loading…' : 'Select Outcome'}</option>
                    {callOutcomeOptions.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={callFormData.notes || ''}
                    onChange={(e) => setCallFormData({ ...callFormData, notes: e.target.value })}
                    className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                    rows={4}
                    placeholder="Enter call notes..."
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Next Call Date</label>
                  <input
                    type="datetime-local"
                    value={callFormData.next_call_date ? (() => {
                      const date = new Date(callFormData.next_call_date);
                      const year = date.getFullYear();
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const day = String(date.getDate()).padStart(2, '0');
                      const hours = String(date.getHours()).padStart(2, '0');
                      const minutes = String(date.getMinutes()).padStart(2, '0');
                      return `${year}-${month}-${day}T${hours}:${minutes}`;
                    })() : ''}
                    onChange={(e) => {
                      if (e.target.value) {
                        setCallFormData({ ...callFormData, next_call_date: datetimeLocalToMysql(e.target.value) });
                      } else {
                        setCallFormData({ ...callFormData, next_call_date: undefined });
                      }
                    }}
                    className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                  />
                </div>
                {callFormData.next_call_date && (
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Next Call Notes</label>
                    <textarea
                      value={callFormData.next_call_notes || ''}
                      onChange={(e) => setCallFormData({ ...callFormData, next_call_notes: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                      rows={2}
                      placeholder="Notes for next call..."
                    />
                  </div>
                )}
                <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-3 sm:pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCallModal(false)}
                    className="px-3 py-1.5 sm:px-4 sm:py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-3 py-1.5 sm:px-4 sm:py-2 bg-yellow-500 text-black-950 rounded-md hover:bg-yellow-600 text-sm font-medium font-bold"
                  >
                    Log Call
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default TenderAwardeeDetailPage;
